// PlatformIssueStore — Issue Tracker + Bug Report state, backed by the
// backend /api/platform-issues surface (Task #237).
//
// - Real submissions (global Bug Report button + the internal "Report Issue"
//   form) are persisted server-side so they survive refresh and reach staff.
// - The staff Issue Tracker loads persisted issues via the shared API client
//   in admin context (staff session cookie / break-glass admin code); non-staff
//   callers simply get an empty server list.
// - Seeded demo issues remain available in PITCH MODE ONLY, so the demo
//   still has content while real deployments show real reports.
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { PlatformIssue, PlatformIssueCategory, IssueStatus, ReporterType, PLATFORM_ISSUES as SEED_ISSUES } from './seed';
import { api } from '../api/client';
import { usePitchMode } from '../hooks/usePitchMode';

interface ServerIssue {
    id: number;
    title: string;
    description: string;
    urgency: 'HIGH' | 'MEDIUM' | 'LOW';
    issue_type: PlatformIssueCategory;
    reporter_name: string;
    reporter_type: ReporterType;
    site_name?: string | null;
    page_name?: string | null;
    status: IssueStatus;
    resolution?: string | null;
    resolved_at?: string | null;
    submitted_at?: string | null;
}

function fromServer(row: ServerIssue): PlatformIssue {
    return {
        id: `srv-${row.id}`,
        title: row.title,
        description: row.description,
        urgency: row.urgency,
        issueType: row.issue_type,
        reporterName: row.reporter_name,
        reporterType: row.reporter_type,
        siteName: row.site_name || undefined,
        submittedAt: (row.submitted_at || '').slice(0, 10),
        status: row.status,
        resolution: row.resolution || undefined,
        resolvedAt: row.resolved_at ? row.resolved_at.slice(0, 10) : undefined,
    };
}

export interface BugReportInput {
    description: string;
    pageName?: string;
    title?: string;
    issueType?: PlatformIssueCategory;
    reporterName?: string;
}

export interface InternalIssueInput {
    title: string;
    description: string;
    urgency: 'HIGH' | 'MEDIUM' | 'LOW';
    issueType: PlatformIssueCategory;
    reporterName: string;
}

interface PlatformIssueContextValue {
    issues: PlatformIssue[];
    /** Public bug report (global Bug Report button). Persists server-side. */
    submitBugReport: (input: BugReportInput) => Promise<boolean>;
    /** Internal issue form (staff). Persists server-side via the admin-context client. */
    createIssue: (input: InternalIssueInput) => Promise<boolean>;
    refresh: () => Promise<void>;
}

const PlatformIssueContext = createContext<PlatformIssueContextValue>({
    issues: [],
    submitBugReport: async () => false,
    createIssue: async () => false,
    refresh: async () => {},
});

export function PlatformIssueProvider({ children }: { children: React.ReactNode }) {
    const pitchMode = usePitchMode();
    const [serverIssues, setServerIssues] = useState<PlatformIssue[]>([]);

    const refresh = useCallback(async () => {
        try {
            // Shared API client (Synterra A3): admin context (staff session cookie
            // / break-glass code). A non-staff 403/401 or backend-down throws and
            // is swallowed → keep whatever issues we already have.
            const rows = await api.get<ServerIssue[]>('/api/platform-issues', { context: 'admin' });
            setServerIssues((rows || []).map(fromServer));
        } catch {
            // Non-staff / backend unreachable — leave current state untouched.
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const submitBugReport = useCallback(async (input: BugReportInput): Promise<boolean> => {
        try {
            // Public context: the session cookie still travels (attaching reporter
            // identity when present) but we don't fire the site session-expired
            // seam for an anonymous bug report from the preview shell.
            const row = await api.post<ServerIssue>(
                '/api/platform-issues/report',
                {
                    description: input.description,
                    page_name: input.pageName || null,
                    title: input.title || null,
                    issue_type: input.issueType || null,
                    reporter_name: input.reporterName || null,
                },
                { context: 'public' },
            );
            setServerIssues(prev => [fromServer(row), ...prev]);
            return true;
        } catch {
            return false;
        }
    }, []);

    const createIssue = useCallback(async (input: InternalIssueInput): Promise<boolean> => {
        try {
            // Admin context: staff session cookie / break-glass code + initiative scoping.
            const row = await api.post<ServerIssue>(
                '/api/platform-issues',
                {
                    title: input.title,
                    description: input.description,
                    urgency: input.urgency,
                    issue_type: input.issueType,
                    reporter_name: input.reporterName,
                },
                { context: 'admin' },
            );
            setServerIssues(prev => [fromServer(row), ...prev]);
            return true;
        } catch {
            return false;
        }
    }, []);

    // Demo seeds only in pitch mode; real deployments show persisted issues only.
    const issues = pitchMode ? [...serverIssues, ...SEED_ISSUES] : serverIssues;

    return (
        <PlatformIssueContext.Provider value={{ issues, submitBugReport, createIssue, refresh }}>
            {children}
        </PlatformIssueContext.Provider>
    );
}

export function usePlatformIssues() {
    return useContext(PlatformIssueContext);
}
